import React, {useEffect, useRef, useState} from 'react';
import TemplateDefault from "../../components/template-default";
import Card from "../../components/card";
import SelectComponent from "../../components/select-component";
import InputForm from "../../components/input-form";
import {handleFilters} from "../../utils/handle-filters";
import useFetchData from "./hooks/use-fetch-data";
import {AgGridReact} from "ag-grid-react";
import {LocalizationAgGrid} from "../../utils/localization-ag-grid";
import CodeCellRenderer from "../../components/cell-renderer/code-cell-renderer";
import ProjectCellRenderer from "../../components/cell-renderer/project-cell-renderer";
import ActivationCellRenderer from "./components/activation-cell-renderer";
import Button from "../../components/button";
import {FaFileExcel} from "react-icons/fa";
import general from "../../utils/general";
import sshService from "../../services/ssh-service";
import SshModal from "../../components/ssh-modal";
import InputCheckbox from "../../components/input-checkbox";

const modalityList = [
    {value: 'ACQUISITION', label: 'Aquisição'},
    {value: 'DEMONSTRATION', label: 'Demonstração'},
    {value: 'DEVELOPMENT', label: 'Desenvolvimento'},
    {value: 'LOCATION', label: 'Locação'},
    {value: 'RELATED', label: 'Relacionada'},
]

const initialFilters = {
    project: "",
    ibge: "",
    modality: [
        {value: 'ACQUISITION', label: 'Aquisição'},
        {value: 'DEMONSTRATION', label: 'Demonstração'},
        {value: 'LOCATION', label: 'Locação'},
        {value: 'RELATED', label: 'Relacionada'},
    ],
    resales: []
}

const DashboardLicense = () => {
    const [filters, setFilters] = useState(initialFilters);
    const [allProjects, setAllProjects] = useState(true);
    const [licenses, setLicenses] = useState([])
    const {licensesFetch, projects, resales} = useFetchData();

    const gridRef = useRef();

    const [columnDefs, setColumnDefs] = useState([]);

    const [sshModalData, setSshModalData] = useState(null);
    const [sshModal, setSshModal] = useState(false);

    const openConnection = (data, id) => {
        setSshModalData(data);
        const ssh = data.ssh;
        if (ssh.length > 1) {
            setSshModal(true);
            return;
        }

        sshService.openSshConnection(id, ssh[0]?.id);
    }

    const columns = [
        {field: "name", headerName: "Projeto", cellRenderer: ProjectCellRenderer, flex: 1 , minWidth: 120},
        {field: "ibge", headerName: "ID", cellRenderer: CodeCellRenderer,
            cellRendererParams: {
                openConnection: openConnection,
            }, flex: 1, minWidth: 100},
        {field: "resales", headerName: "Revendas", flex: 1, minWidth: 100},
        {field: "modality_lang", headerName: "Modalidade", flex: 1, minWidth: 80},
        {
            field: "until", headerName: "Fim do contrato", flex: 1, minWidth: 90,
            cellRenderer: ({data, value}) => {
                const {until_status} = data;
                return statusDates(until_status, value);
            },
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return general.nullLastDateStringComparator(a, b, nodeA, nodeB, isInverted);
            }
        },
        {
            field: "expiration", headerName: "Expiração", flex: 1, minWidth: 90,
            cellRenderer: ({data, value}) => {
                const {expiration_status} = data;
                return statusDates(expiration_status, value);
            },
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return general.nullLastDateStringComparator(a, b, nodeA, nodeB, isInverted);
            }
        },
        {field: "activation", headerName: "Ativação", flex: 1, minWidth: 90, cellRenderer: ActivationCellRenderer,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return general.nullLastDateStringComparator(a, b, nodeA, nodeB, isInverted);
            }
        },
        {field: "type", headerName: "Tipo", width: 90},
    ];

    useEffect(() => {
        const orderColumns = JSON.parse(localStorage.getItem("license-order-columns"));
        const widthColumns = JSON.parse(localStorage.getItem("license-width-columns"));
        if (orderColumns) general.sortColumns(columns, orderColumns);
        if (widthColumns){
            columns.map((element, index) => {
                if (element.minWidth) {
                    delete element.flex;
                    element.width = widthColumns[index];
                }
                if (element.width) element.width = widthColumns[index]

                return columns
            })
        }
        setColumnDefs(columns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (licensesFetch) {
            setLicenses(handleFilters(licensesFetch, filters, true))
        }
    }, [filters, licensesFetch]);

    const statusDates = (status, value) => {
        if (status === 'expired') {
            return <span className="font-bold text-red-600">{value}</span>;
        } else if (status === 'to-expired') {
            return <span className="font-bold text-orange-600">{value}</span>;
        }

        return value;
    }

    const handleSelect = (id, value) => setFilters(prev => ({...prev, [id]: value}));
    const handleInput = ({target: {id, value}}) => setFilters(prev => ({...prev, [id]: value}));

    const exportLicensing = () => {
        const licensesToExport = allProjects
            ? licensesFetch
            : licensesFetch.filter(data => !data.dead_file);
        const newArray = licensesToExport.map(data =>
            ({
                "Projeto": data.name,
                "ID": data.ibge,
                "Revendas": data.resales,
                "Modalidade": data.modality_lang,
                "Fim do contrato": data.until,
                "Expiração": data.expiration,
                "Ativação": data.activation,
                "Tipo": data.type,
                "Arquivo morto": data.dead_file ? "Sim" : "Não",
            })
        );

        general.exportCsv(newArray);
    }

    const onColumnMoved = (params) => {
        const columns = params.api.getAllDisplayedColumns().map(col => col.colId);
        localStorage.setItem(`license-order-columns`, JSON.stringify(columns))
    }

    const columnResizedHandler = (params) => {
        const width = params.api.getAllDisplayedColumns().map(col => col.actualWidth);
        localStorage.setItem("license-width-columns", JSON.stringify(width))
    };

    return (
        <TemplateDefault title={"Dashboard - Licenças"}>
            <Card>
                <div className="flex flex-col lg:grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-12">
                    <div className="col-span-2 text-gray-900">
                        <SelectComponent
                            isCreatable
                            instanceId="projects"
                            label="Projeto"
                            options={projects ?? []}
                            value={filters.project}
                            onChange={(value) => handleSelect("project", value)}
                            name="project"
                            formatCreateLabel={(inputValue) => `${inputValue}`}
                        />
                    </div>

                    <InputForm id="ibge" col="col-span-2" label="ID" type="search" onChange={handleInput}/>

                    <div className="col-span-3 text-gray-900">
                        <SelectComponent
                            isMulti
                            instanceId="modality"
                            label="Modalidade"
                            options={modalityList}
                            value={filters.modality}
                            onChange={(value) => handleSelect("modality", value)}
                        />
                    </div>

                    <div className="col-span-5 flex gap-4">
                            <SelectComponent
                                isMulti
                                instanceId="resales"
                                label="Revenda"
                                col={"w-full"}
                                options={resales ?? []}
                                value={filters.resales}
                                onChange={(value) => handleSelect("resales", value)}
                            />

                        <InputCheckbox
                            id="allProjects"
                            name="allProjects"
                            label="Exportar arquivo morto"
                            onChange={({target: {checked}}) => setAllProjects(checked)}
                            checked={allProjects}
                            col={"w-[268px]"}
                        />

                        <Button target="success" onClick={exportLicensing} col="!h-[32px]">
                            <FaFileExcel/>
                        </Button>
                    </div>

                    <div className={"col-span-12"} style={{minHeight: "calc(100vh - 185px)"}}>
                        <div className={"ag-theme-quartz grid"}>
                            <AgGridReact
                                ref={gridRef}
                                rowHeight={32}
                                rowData={licenses ?? []}
                                columnDefs={columnDefs}
                                animateRows={false}
                                suppressRowClickSelection={true}
                                suppressCellFocus={true}
                                domLayout='autoHeight'
                                gridOptions={{localeText: LocalizationAgGrid}}
                                onColumnMoved={onColumnMoved}
                                onColumnResized={columnResizedHandler}
                            />
                        </div>
                    </div>
                </div>
            </Card>

            <SshModal open={sshModal} set={setSshModal} project={sshModalData}/>

        </TemplateDefault>
    );
};

export default DashboardLicense;
